import { useId, useState } from "react"
import {
  CHOOSE_CAREADVOCATE_CALLBACK_TO_CANCEL_ACTION,
  CHOOSE_CAREADVOCATE_CALLBACK_TO_RESCHEDULE_ACTION,
  type CareAdvocateCallback,
  type CareAdvocateCallbackPickerProps,
} from "@/types"
import { cn } from "@/utils/cn"

const HEADING_BY_MODE = {
  view: "Your upcoming Care Advocate calls",
  reschedule: "Which call do you want to move?",
  cancel: "Which call do you want to cancel?",
} as const

/**
 * The member's upcoming Care Advocate calls. In "view" mode the calls are listed
 * for reading only. In "reschedule" or "cancel" mode each call is a button, and a
 * tap emits the one action matching the mode with the call's label and
 * callbackId; the chat layer turns that into the message. Further taps lock
 * until the conversation moves on.
 */
export function CareAdvocateCallbackPicker({ data, onAction }: CareAdvocateCallbackPickerProps) {
  const [selectedCallbackId, setSelectedCallbackId] = useState<number | null>(null)
  const headingId = useId()

  if (data.callbacks.length === 0) {
    return null
  }

  const isChoosing = data.mode !== "view"

  const handleSelect = (callback: CareAdvocateCallback) => {
    if (selectedCallbackId !== null) return
    setSelectedCallbackId(callback.callbackId)
    onAction?.(
      data.mode === "cancel"
        ? CHOOSE_CAREADVOCATE_CALLBACK_TO_CANCEL_ACTION
        : CHOOSE_CAREADVOCATE_CALLBACK_TO_RESCHEDULE_ACTION,
      { label: callback.label, callbackId: callback.callbackId },
    )
  }

  return (
    <section
      className="w-full rounded-lg border border-gray-200 bg-white p-4 shadow-sm"
      aria-labelledby={headingId}
    >
      <h3 id={headingId} className="text-sm font-semibold text-gray-900">
        {HEADING_BY_MODE[data.mode]}
      </h3>
      <ul className="mt-2 space-y-2">
        {data.callbacks.map((callback) => {
          if (!isChoosing) {
            return (
              <li
                key={callback.callbackId}
                className="rounded-md border border-gray-200 bg-gray-50 px-3 py-2 text-sm text-gray-900"
              >
                {callback.label}
              </li>
            )
          }
          const isSelected = callback.callbackId === selectedCallbackId
          return (
            <li key={callback.callbackId}>
              <button
                type="button"
                disabled={selectedCallbackId !== null && !isSelected}
                aria-pressed={isSelected}
                onClick={() => handleSelect(callback)}
                className={cn(
                  "w-full rounded-md border px-3 py-2 text-left text-sm cursor-pointer",
                  "transition-colors focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-1",
                  "disabled:cursor-not-allowed disabled:opacity-50",
                  isSelected
                    ? "border-primary-600 bg-primary-600 text-white"
                    : "border-primary-200 bg-white text-primary-700 hover:bg-primary-50",
                )}
              >
                {callback.label}
              </button>
            </li>
          )
        })}
      </ul>
    </section>
  )
}
